import { type IAPIResponse, type ILocation } from "@/types";
import type { MediworksSourceFilterBody } from "@/types/mediworksSourceFilter";
import type { IFetchIdNameResponse } from "@/types/memberPortal";
import { baseApi } from "@/core/api/baseApi";
import { toUniqueIdNamePairs, type IdName } from "@/shared/lib/idName";

export interface ILocationParams {
  name?: string;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  isActive?: string;
  pageNo?: number;
  pageSize?: number;
}

interface IGlobalSearchParams {
  query: string;
  pageNo?: number;
  pageSize?: number;
}

/** `locationId` narrows the list to that single location. */
export interface LocationIdNameParams {
  clientId: number;
  employerId: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
}

export const locationsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getLocations: build.query<IAPIResponse<ILocation>, ILocationParams>({
      query: (body) => ({
        url: `/mediworks/locations`,
        method: "POST",
        body,
      }),
    }),
    getLocationsBySource: build.query<
      IAPIResponse<ILocation>,
      { entityId: number; body: MediworksSourceFilterBody }
    >({
      query: ({ entityId, body }) => ({
        url: `mediworks/location/${entityId}`,
        method: "GET",
        body,
      }),
      transformResponse: (response: {
        data?: ILocation | ILocation[];
        totalCount?: number;
        [key: string]: unknown;
      }): IAPIResponse<ILocation> => {
        const data = response.data;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        return {
          data: list,
          totalCount: response.totalCount ?? list.length,
          pageNo: 1,
          pageSize: list.length || 10,
          currentPage: 1,
          totalPages: list.length ? 1 : 0,
        };
      },
    }),
    searchGlobalLocations: build.query<IAPIResponse<ILocation>, IGlobalSearchParams>({
      query: ({ query, pageNo = 1, pageSize = 100 }) => ({
        url: `/mediworks/locations/global-search`,
        method: "GET",
        params: { query, pageNo, pageSize },
      }),
    }),
    listLocations: build.query<{ data: IFetchIdNameResponse }, void>({
      query: () => ({
        url: `/mediworks/locations/fetch-id-name`,
        method: "GET",
      }),
    }),
    /** The locations under one scope, id and name only. See `getGroupsIdName`. */
    getLocationsIdName: build.query<IdName[], LocationIdNameParams>({
      query: ({ clientId, employerId, groupId, divisionId, locationId }) => ({
        url: `/mediworks/locations/id-name`,
        method: "GET",
        params: {
          clientId,
          employerId,
          ...(groupId != null && { groupId }),
          ...(divisionId != null && { divisionId }),
          ...(locationId != null && { locationId }),
        },
      }),
      transformResponse: (r: unknown) => toUniqueIdNamePairs(r, "locations", "locationId"),
    }),
    /**
     * The locations under several employers, id and name only, merged by id — one id-name call per
     * distinct employer. The platform-wide `fetch-id-name` list is keyed by NAME and collapses
     * same-named rows into one, so it cannot be used to name an id; ids under an employer never collide.
     */
    getLocationsIdNameForScopes: build.query<IdName[], { clientId: number; employerId: number }[]>({
      queryFn: async (scopes, _api, _extra, fetchWithBQ) => {
        const unique = new Map<string, { clientId: number; employerId: number }>();
        for (const { clientId, employerId } of scopes) {
          const key = `${clientId}:${employerId}`;
          if (!unique.has(key)) unique.set(key, { clientId, employerId });
        }
        // Every employer at once rather than one after another; merged in the order asked, so the
        // first employer still wins an id two of them share.
        const results = await Promise.all(
          [...unique.values()].map(({ clientId, employerId }) =>
            fetchWithBQ({
              url: `/mediworks/locations/id-name`,
              method: "GET",
              params: { clientId, employerId },
            })
          )
        );
        const byId = new Map<number, IdName>();
        for (const res of results) {
          // One employer failing must not blank every other employer's names.
          if (res.error) continue;
          for (const row of toUniqueIdNamePairs(res.data, "locations", "locationId"))
            if (!byId.has(row.id)) byId.set(row.id, row);
        }
        return { data: [...byId.values()] };
      },
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useGetLocationsBySourceQuery,
  useSearchGlobalLocationsQuery,
  useListLocationsQuery,
  useGetLocationsIdNameQuery,
  useGetLocationsIdNameForScopesQuery,
} = locationsApi;
